/**
 * Prompt Generator
 *
 * Client-side service for AI-powered Suno prompt generation using the
 * generate-song-content edge function.
 *
 * Handles style influence translation (e.g., "Metallica + Chopin")
 * without including artist names in the output.
 */

import { SUPABASE_URL } from '../../config/config.js';
import { authService } from '../../auth/auth-service.js';

/**
 * Prompt generation options
 * @typedef {Object} PromptOptions
 * @property {string[]} genres - Primary genres
 * @property {string[]} [subGenres] - Sub-genres or variations
 * @property {string} [mood] - Mood/atmosphere
 * @property {string} [tempo] - Tempo description
 * @property {string[]} [instruments] - Key instruments
 * @property {Object} [vocals] - Vocal specifications
 * @property {string} [vocals.type] - Vocal type (e.g., "female", "male", "choir")
 * @property {string} [vocals.style] - Vocal style (e.g., "powerful", "soft")
 * @property {string[]} [styleInfluences] - Artists/songs for style reference (won't appear in output)
 * @property {string} [era] - Era/period influence
 * @property {string} [additionalNotes] - Any additional specifications
 */

/**
 * Prompt generation result
 * @typedef {Object} PromptResult
 * @property {string} prompt - The generated Suno prompt
 * @property {Object} metadata - Generation metadata
 */

/**
 * Generate a Suno prompt using AI
 * @param {PromptOptions} options - Generation options
 * @returns {Promise<PromptResult>}
 */
export async function generatePrompt(options) {
  const { genres, subGenres, mood, tempo, instruments, vocals, styleInfluences, era, additionalNotes } = options;

  if (!genres || !Array.isArray(genres) || genres.length === 0) {
    throw new Error('At least one genre is required');
  }

  // Get auth token
  const session = await authService.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required for prompt generation');
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-song-content`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify({
      type: 'prompt',
      genres,
      subGenres: subGenres || undefined,
      mood: mood || undefined,
      tempo: tempo || undefined,
      instruments: instruments || undefined,
      vocals: vocals || undefined,
      styleInfluences: styleInfluences || undefined,
      era: era || undefined,
      additionalNotes: additionalNotes || undefined
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Generation failed: ${response.status}`);
  }

  const data = await response.json();

  return {
    prompt: data.content,
    metadata: data.metadata || {}
  };
}

/**
 * Genre taxonomy - primary genres with sub-genres
 */
export const GENRE_MAP = {
  'Electronic': [
    'House', 'Deep House', 'Tech House', 'Progressive House',
    'Techno', 'Melodic Techno', 'Minimal Techno',
    'Drum & Bass', 'Liquid DnB', 'Neurofunk',
    'Dubstep', 'Melodic Dubstep', 'Riddim',
    'Trance', 'Progressive Trance', 'Psytrance',
    'Ambient', 'Dark Ambient', 'Space Ambient',
    'Synthwave', 'Darksynth', 'Retrowave',
    'Downtempo', 'Trip Hop', 'Chillout'
  ],
  'Rock': [
    'Classic Rock', 'Blues Rock', 'Southern Rock',
    'Hard Rock', 'Heavy Metal', 'Glam Rock',
    'Alternative', 'Grunge', 'Shoegaze', 'Post-Rock',
    'Punk', 'Pop Punk', 'Post-Punk', 'Emo',
    'Progressive Rock', 'Art Rock',
    'Indie Rock', 'Garage Rock'
  ],
  'Pop': [
    'Contemporary Pop', 'Dance Pop', 'Electropop',
    'Synth Pop', 'Bedroom Pop', 'Art Pop',
    'K-Pop', 'J-Pop', 'City Pop'
  ],
  'Hip-Hop': [
    'Boom Bap', 'East Coast', 'Golden Age',
    'Trap', 'Melodic Trap', 'Drill',
    'Lo-fi Hip Hop', 'Chillhop', 'Jazzhop',
    'Conscious Hip-Hop', 'Alternative Hip-Hop'
  ],
  'R&B & Soul': [
    'Contemporary R&B', 'Alternative R&B',
    'Neo Soul', 'Classic Soul', 'Motown',
    'Funk', 'Synth Funk', 'G-Funk'
  ],
  'Jazz': [
    'Bebop', 'Cool Jazz', 'Swing',
    'Jazz Fusion', 'Smooth Jazz', 'Nu Jazz',
    'Free Jazz', 'Acid Jazz'
  ],
  'Classical': [
    'Orchestral', 'Symphonic', 'Film Score',
    'Classical Piano', 'Romantic Piano',
    'Minimalist', 'Modern Classical'
  ],
  'Folk & Acoustic': [
    'Indie Folk', 'Folk Rock', 'Singer-Songwriter',
    'Celtic', 'Americana', 'Bluegrass'
  ],
  'World': [
    'Latin', 'Reggaeton', 'Bossa Nova',
    'Afrobeat', 'Amapiano',
    'Reggae', 'Dancehall', 'Dub'
  ],
  'Metal': [
    'Heavy Metal', 'Death Metal', 'Black Metal',
    'Progressive Metal', 'Djent',
    'Nu Metal', 'Industrial Metal'
  ]
};

/**
 * Mood options
 */
export const MOODS = [
  'Energetic', 'Uplifting', 'Euphoric', 'Powerful', 'Intense', 'Driving',
  'Calm', 'Peaceful', 'Serene', 'Meditative', 'Soothing', 'Relaxing',
  'Dark', 'Brooding', 'Melancholic', 'Haunting', 'Mysterious', 'Ominous',
  'Happy', 'Joyful', 'Playful', 'Fun', 'Cheerful',
  'Emotional', 'Nostalgic', 'Bittersweet', 'Romantic', 'Tender',
  'Atmospheric', 'Dreamy', 'Ethereal', 'Spacey', 'Cinematic', 'Epic'
];

/**
 * Tempo options
 */
export const TEMPOS = [
  'Very Slow (60-80 BPM)',
  'Slow (80-100 BPM)',
  'Medium (100-120 BPM)',
  'Upbeat (120-140 BPM)',
  'Fast (140-160 BPM)',
  'Very Fast (160+ BPM)'
];

/**
 * Instrument categories
 */
export const INSTRUMENTS = {
  'Lead': ['Piano', 'Synthesizer', 'Electric Guitar', 'Acoustic Guitar', 'Violin', 'Saxophone', 'Trumpet', 'Flute'],
  'Rhythm': ['Drums', 'Bass Guitar', 'Synth Bass', '808s', 'Percussion', 'Congas'],
  'Texture': ['Strings', 'Brass Section', 'Choir', 'Pads', 'Arpeggios', 'Plucks']
};

/**
 * Vocal options
 */
export const VOCALS = {
  types: ['Male', 'Female', 'Duet', 'Choir', 'Falsetto', 'Baritone', 'Soprano', 'Alto', 'Tenor', 'Bass'],
  styles: ['Powerful', 'Soft', 'Raspy', 'Smooth', 'Airy', 'Belting', 'Whispered', 'Spoken Word', 'Harmonized', 'Auto-tuned']
};

/**
 * Era/period options
 */
export const ERAS = [
  '60s', '70s', '80s', '90s', '2000s', '2010s',
  'Vintage', 'Retro', 'Classic', 'Modern', 'Contemporary', 'Futuristic'
];

/**
 * Get all genres as a flat list
 */
export function getAllGenres() {
  const genres = [];
  for (const [category, subGenres] of Object.entries(GENRE_MAP)) {
    genres.push(category);
    genres.push(...subGenres);
  }
  return [...new Set(genres)];
}

/**
 * Get all instruments as a flat list
 */
export function getAllInstruments() {
  const instruments = [];
  for (const category of Object.values(INSTRUMENTS)) {
    instruments.push(...category);
  }
  return instruments;
}

export default {
  generatePrompt,
  GENRE_MAP,
  MOODS,
  TEMPOS,
  INSTRUMENTS,
  VOCALS,
  ERAS,
  getAllGenres,
  getAllInstruments
};
